/**
 * Minimal HTML sanitization for QTI content.
 *
 * IMPORTANT: QTI content comes from a trusted source (the QTI authoring system).
 * The renderer's job is to render, not to be a security gate.
 *
 * This sanitizer ONLY removes actually dangerous patterns:
 * - <script> tags
 * - Event handlers (onclick, onerror, onload, etc.)
 * - javascript: protocol in URLs
 * - data:text/html (XSS vector)
 *
 * It does NOT modify:
 * - Style attributes
 * - Image src (including data URIs)
 * - Any other legitimate HTML/MathML content
 *
 * If stricter sanitization is needed, it should happen at the content
 * ingestion layer (when content is authored/imported), not at render time.
 */

/**
 * Sanitizes HTML by removing only actually dangerous XSS patterns.
 * Preserves all legitimate content including styles, data URIs, etc.
 */
export function sanitizeHtml(html: string): string {
	if (!html) return html

	return (
		html
			// Remove <script> tags and their contents
			.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, "")
			// Remove event handlers (onclick, onerror, onload, onmouseover, etc.)
			.replace(/\s+on\w+\s*=\s*["'][^"']*["']/gi, "")
			.replace(/\s+on\w+\s*=\s*[^\s>]+/gi, "")
			// Remove javascript: protocol in href/src
			.replace(/\b(href|src)\s*=\s*["']?\s*javascript:[^"'>\s]*/gi, "")
			// Remove data:text/html (XSS vector, but keep data:image/* for images)
			.replace(/\b(href|src)\s*=\s*["']?\s*data:text\/html[^"'>\s]*/gi, "")
			// Remove <iframe>, <embed>, <object> tags (common XSS vectors)
			.replace(/<(iframe|embed|object)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
			.replace(/<(iframe|embed|object)\b[^>]*\/?>/gi, "")
	)
}

/**
 * Sanitizes HTML for safe display.
 * This is now just an alias for sanitizeHtml since we use a unified approach.
 */
export function sanitizeForDisplay(html: string): string {
	return sanitizeHtml(html)
}
